import { Avatar, Button } from '@efexteam/components';
import { Actions, Can, Subjects } from '@efexteam/permission-manager';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Grid2, Typography } from '@mui/material';
import { selectProfileData } from '@pages/profile/store/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SearchClientInput from '@/modules/receivables/SearchClientInput';
import { NEW_USER_PERSON } from '../../../../constants/clients';
import useImmediateApiCall from '../../../../hooks/useImmediateApiCall';
import clientSvc from '../../../../services/client/clientSvc';
import { IClientModal } from '../../../../services/client/types';
import DataLoadingWrapper from '../../../common/DataLoadingWrapper';
import { useFormData } from '../context';

interface SelectClientProps {
  nextFormStep: () => void;
}

type FormData = {
  client: IClientModal;
};

const SelectClient = ({ nextFormStep }: SelectClientProps) => {
  const { t } = useTranslation();
  const { setFormValues, data } = useFormData();
  const navigate = useNavigate();
  const userProfile = useSelector(selectProfileData);
  const [options, setOptions] = useState<IClientModal[]>([]);

  const enterpriseId = useMemo(() => {
    if (userProfile?.company) return userProfile?.company?.enterpriseId;
    return null;
  }, [userProfile]);

  const { data: clientsModal, loading: loadingClients } = useImmediateApiCall(
    () => clientSvc.searchContactByEmail('', enterpriseId || '')
  );

  const { handleSubmit, setValue } = useForm<FormData>();

  const onSubmit = handleSubmit((values) => {
    setFormValues({ ...data, client: values.client });
    nextFormStep();
  });

  useEffect(() => {
    if (data.client) {
      setValue('client', data.client);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (clientsModal) {
      const clientPayable = [
        ...new Map(
          clientsModal.clientPayableData.map((m) => [m.name, m])
        ).values(),
      ];
      const fixedOptions = clientPayable
        .filter((entity: { origin: string }) => entity.origin.match('clients'))
        .reverse();
      setOptions(fixedOptions);
    }
  }, [clientsModal]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('textSearchEmail')}
      </Typography>

      <DataLoadingWrapper
        className="min-h-[275px] flex justify-center items-center"
        loadingData={loadingClients}
      >
        <form onSubmit={onSubmit} autoComplete="off">
          <Grid2 container gap={3}>
            <Grid2 size={12}>
              <SearchClientInput
                className="w-full flex justify-center"
                onSelectClient={(client) => setFormValues({ ...data, client })}
              />
              <Can action={Actions.CREATE} subject={Subjects.BENEFICIARIES}>
                <Button
                  className="w-full !justify-between border-[1.5px]"
                  variant="outlined"
                  appearance="primary"
                  type="button"
                  onClick={() => navigate('/clients?action=new_client')}
                  endIcon={<AddIcon />}
                >
                  {t('addBusiness')}
                </Button>
              </Can>
            </Grid2>
            <Grid2 className="max-h-[200px] overflow-auto" size={12}>
              {options.map((element) => (
                <Button
                  appearance="primary"
                  variant="outlined"
                  type="button"
                  key={element.uuid}
                  className={`w-full !justify-start !items-center gap-2 !mb-1 !py-1 !h-auto ${
                    data.client?.uuid === element.uuid ? '!bg-slate-300' : ''
                  }`}
                  onClick={() => setFormValues({ ...data, client: element })}
                >
                  <Avatar
                    username={
                      element.companyName || element.email || element.name || ''
                    }
                    size="sm"
                  />
                  <Typography>
                    {element.entityBusiness === NEW_USER_PERSON
                      ? element.name
                      : element.companyName || element.email || element.name}
                  </Typography>
                  {data.client?.uuid === element.uuid && (
                    <CheckCircleOutlineIcon
                      fontSize="small"
                      className="ml-auto"
                    />
                  )}
                </Button>
              ))}
            </Grid2>
          </Grid2>
          <Grid2 size={12} className="flex justify-end mt-2">
            <Button
              appearance="primary"
              disabled={!data.client}
              size="large"
              className="mt-4"
              type="submit"
            >
              {t('continue')}
            </Button>
          </Grid2>
        </form>
      </DataLoadingWrapper>
    </>
  );
};

export default SelectClient;
